import React, { useEffect, useMemo, useRef, useState } from 'react';
import './style.scss';
import useWindowSize from '../../../Shared/hooks/useWindowSize';
import { CardsListGridCard, CardsListGridCardRewards, CardsListGridPlaceholder } from './card';
import clsx from 'clsx';
import { v1 as uuid } from 'uuid';

const CardsListGridView = ({
  cards,
  selectCard,
  cardsSelected,
  selectable,
  rewardsOpenedFor,
  openRewards,
  dontShowCompare,
  t,
}) => {
  const [sizeX] = useWindowSize();

  const divider = useMemo(() => {
    let newValue = 4;
    if (sizeX < 1750) newValue = 3;
    if (sizeX < 1280) newValue = 2;
    if (sizeX < 768) newValue = 1;
    return newValue;
  }, [sizeX]);

  const toRender = useMemo(() => {
    return splitArray(cards, divider);
  }, [divider]);

  return (
    <div className={clsx('cards-list-grid-cards-wrapper', 'content')}>
      {toRender.map((row) => (
        <div className="clgc-row-wrapper" key={uuid()}>
          <CardsRow
            row={row}
            selectCard={selectCard}
            cardsSelected={cardsSelected}
            openRewards={openRewards}
            rewardsOpenedFor={rewardsOpenedFor}
            selectable={selectable}
            dontShowCompare={dontShowCompare}
            t={t}
          />
          <RewardsRow row={row} rewardsOpenedFor={rewardsOpenedFor} />
        </div>
      ))}
    </div>
  );
};

const CardsRow = ({
  row,
  selectCard,
  cardsSelected,
  selectable,
  rewardsOpenedFor,
  openRewards,
  dontShowCompare,
  t,
}) => (
  <div className="clgc-row">
    {row.map((card, index) => {
      if (!card) return <CardsListGridPlaceholder key={uuid()} />;
      return (
        <CardsListGridCard
          data={card}
          key={index}
          dontShowCompare={dontShowCompare}
          onSelect={() => selectCard(card)}
          isSelected={cardsSelected.includes(card.id)}
          selectable={selectable}
          rewardsOpened={rewardsOpenedFor === card.id}
          openRewards={() => openRewards(card.id)}
          t={t}
        />
      );
    })}
  </div>
);

const RewardsRow = ({ row, rewardsOpenedFor }) => {
  const [middlewareId, setMiddlewareId] = useState(null);
  const idRef = useRef(null);

  useEffect(() => {
    let timeout;
    const updateId = (id) => {
      setMiddlewareId(id);
      idRef.current = id;
    };
    if (!rewardsOpenedFor) {
      //if all rewards are closed
      updateId(null); //closing opened rewards
    } else {
      //looking for card in this row
      const card = row.find((c) => c?.id && c.id === rewardsOpenedFor);
      if (card) {
        //if cards belongs to this row
        if (idRef.current) {
          //and another card rewards are opened
          updateId(null); //closing rewards
          timeout = setTimeout(() => updateId(card.id), 200); //after animation opening new card rewards
        } else {
          //if no rewards are opened
          updateId(card.id); //just opening new card rewards
        }
      } else {
        //if card doesn't belong to this row
        updateId(null); //closing opened rewards
      }
    }
    return () => clearTimeout(timeout);
  }, [rewardsOpenedFor]);

  return (
    <div className="clgc-rewards-row">
      {row
        .filter((card) => !!card)
        .map((card) => (
          <CardsListGridCardRewards
            data={card}
            rewardsOpened={middlewareId === card.id}
            key={uuid()}
          />
        ))}
    </div>
  );
};

const splitArray = (input, divider) => {
  const raw = [...input];
  const result = [];
  while (raw.length > 0) {
    const piece = raw.splice(0, divider);
    let toPush = piece;
    if (piece.length < divider) {
      toPush = [...Array(divider)].map((_, index) => piece[index] || null);
    }
    result.push(toPush);
  }
  return result;
};

export default CardsListGridView;
